import React, { useState, useEffect, useRef } from 'react';

import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import { green } from '@material-ui/core/colors';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import {
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@material-ui/core';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import moment from 'moment';
import { isArray, capitalize } from 'lodash';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import AttachFile from '@material-ui/icons/AttachFile';
import { head } from 'lodash';
import ConfirmationModal from '../ConfirmationModal';
import MessageVariablesPicker from '../MessageVariablesPicker';
import useQueues from '../../hooks/useQueues';
import UserStatusIcon from '../UserModal/statusIcon';
import { Facebook, Instagram, WhatsApp, FlashOn } from '@material-ui/icons';
import TemplateMetaModal from '../TemplateMetaModal';

const useStyles = makeStyles((theme) => ({
  root: { display: 'flex', flexWrap: 'wrap' },
  btnWrapper: { position: 'relative' },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

const ScheduleSchema = Yup.object().shape({
  body: Yup.string().min(5, 'Mensagem muito curta').required('Obrigatório'),
  contactId: Yup.number().required('Obrigatório'),
  sendAt: Yup.string().required('Obrigatório'),
  reminderDate: Yup.string().nullable(),
});

const initialContact = { id: '', name: '', channel: '' };

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  cleanContact,
  reload,
  message,
  fromMessageInput = false,
  user,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const isMounted = useRef(true);
  const { companyId } = user;
  const isAdmin = user.profile === 'admin';

  const getInitialState = () => ({
    body: message || '',
    contactId: contactId || '',
    sendAt: moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
    sentAt: '',
    openTicket: 'enabled',
    ticketUserId: user.id,
    queueId: '',
    statusTicket: 'open',
    intervalo: 1,
    valorIntervalo: 0,
    enviarQuantasVezes: 0,
    tipoDias: 4,
    assinar: false,
    reminderDate: '',
    // Campos de template
    templateId: '',
    templateName: '',
    templateBody: '',
    templateData: null,
  });

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  // templates = lista de quick-messages com isOficial: true
  const [templates, setTemplates] = useState([]);
  // selectedTemplate = objeto completo da quick-message selecionada
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(null);
  const [schedule, setSchedule] = useState(getInitialState);
  const [currentContact, setCurrentContact] = useState(initialContact);
  const [contacts, setContacts] = useState([initialContact]);
  const [intervalo, setIntervalo] = useState(1);
  const [tipoDias, setTipoDias] = useState(4);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const messageInputRef = useRef();
  const [channelFilter, setChannelFilter] = useState('all');
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState('');
  const [loading, setLoading] = useState(false);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState('');
  const { findAll: findAllQueues } = useQueues();
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState('');

  // quickMessages (isOficial: false)
  const [quickMessages, setQuickMessages] = useState([]);
  const [filteredQuickMessages, setFilteredQuickMessages] = useState([]);
  const [loadingQuickMessages, setLoadingQuickMessages] = useState(false);
  const [quickMessageSearch, setQuickMessageSearch] = useState('');
  const [showAllQuickMessages, setShowAllQuickMessages] = useState(false);
  const [quickMessageMedia, setQuickMessageMedia] = useState(null);

  const isOficialChannel = selectedWhatsapp?.channel === 'whatsapp_oficial';
  const setFieldValueRef = useRef(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && user?.companyId) fetchQuickMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.companyId, user?.id]);

  useEffect(() => {
    if (quickMessageSearch.trim() === '' && !showAllQuickMessages) {
      setFilteredQuickMessages([]);
    } else if (quickMessageSearch.trim() === '' && showAllQuickMessages) {
      setFilteredQuickMessages(quickMessages);
    } else {
      setFilteredQuickMessages(
        quickMessages.filter(
          (qm) =>
            qm.message
              .toLowerCase()
              .includes(quickMessageSearch.toLowerCase()) ||
            qm.shortcode
              .toLowerCase()
              .includes(quickMessageSearch.toLowerCase()),
        ),
      );
    }
  }, [quickMessageSearch, quickMessages, showAllQuickMessages]);

  // Buscar respostas rápidas normais (isOficial: false)
  const fetchQuickMessages = async () => {
    setLoadingQuickMessages(true);
    try {
      const { data } = await api.get('/quick-messages/list', {
        params: {
          companyId: user?.companyId,
          userId: user?.id,
          isOficial: 'false',
        },
      });
      if (isMounted.current) {
        setQuickMessages(data || []);
        setFilteredQuickMessages(data || []);
      }
    } catch (err) {
      toastError(err);
      if (isMounted.current) {
        setQuickMessages([]);
        setFilteredQuickMessages([]);
      }
    } finally {
      if (isMounted.current) setLoadingQuickMessages(false);
    }
  };

  // Buscar templates oficiais (isOficial: true, status: APPROVED) quando canal oficial é selecionado
  const loadTemplates = async () => {
    try {
      const { data } = await api.get('/quick-messages/list', {
        params: {
          isOficial: 'true',
          userId: user.id,
          companyId: user.companyId,
          status: 'APPROVED',
        },
      });
      if (isMounted.current) setTemplates(data || []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (selectedWhatsapp?.channel === 'whatsapp_oficial') {
      loadTemplates();
    } else {
      setTemplates([]);
      setSelectedTemplate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWhatsapp]);

  const downloadQuickMessageMedia = async (mediaPath, mediaName, mediaType) => {
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
      const response = await fetch(
        `${backendUrl}/public/company${user?.companyId}/quickMessage/${mediaName}`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        },
      );
      if (!response.ok)
        throw new Error(`Erro ao baixar mídia: ${response.status}`);
      const blob = await response.blob();
      const ext = mediaName.split('.').pop().toLowerCase();
      const mimeMap = {
        image: `image/${ext === 'jpg' ? 'jpeg' : ext}`,
        audio: `audio/${ext}`,
        video: `video/${ext}`,
      };
      return new File([blob], mediaName, {
        type: blob.type || mimeMap[mediaType] || 'application/octet-stream',
      });
    } catch (err) {
      toastError(err);
      return null;
    }
  };

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      return;
    }
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get('/users/')
        .then(({ data }) => {
          if (isMounted.current) setOptions(data.users);
        })
        .catch((err) => toastError(err))
        .finally(() => {
          if (isMounted.current) setLoading(false);
        });
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam]);

  useEffect(() => {
    api
      .get('/whatsapp/filter', {
        params: { session: 0, channel: ['whatsapp', 'whatsapp_oficial'] },
      })
      .then(({ data }) => {
        if (!isMounted.current) return;
        const mapped = data.map((w) => ({ ...w, selected: false }));
        setWhatsapps(mapped);
        if (mapped.length === 1) {
          setSelectedWhatsapps(mapped[0].id);
          setSelectedWhatsapp(mapped[0]);
        }
      })
      .catch((err) => toastError(err));
  }, [currentContact, channelFilter]);

  useEffect(() => {
    if (contactId && contacts.length) {
      const c = contacts.find((c) => c.id === contactId);
      if (c) setCurrentContact(c);
    }
  }, [contactId, contacts]);

  useEffect(() => {
    if (!open) return;
    const loadData = async () => {
      try {
        const { data: contactList } = await api.get('/contacts/list', {
          params: { companyId },
        });
        const customList = contactList.map((c) => ({
          id: c.id,
          name: c.name,
          channel: c.channel,
        }));
        if (isMounted.current && isArray(customList)) {
          setContacts([{ id: '', name: '', channel: '' }, ...customList]);
        }

        if (!scheduleId) {
          if (isMounted.current) {
            setSchedule({
              ...getInitialState(),
              body: message || '',
              contactId: contactId || '',
            });
            if (contactId && customList.length > 0) {
              const found = customList.find(
                (c) => c.id.toString() === contactId.toString(),
              );
              if (found) {
                setCurrentContact(found);
                setChannelFilter(found.channel || 'whatsapp');
              }
            }
          }
          return;
        }

        const { data } = await api.get(`/schedules/${scheduleId}`);
        if (!isMounted.current) return;

        setSchedule((prev) => ({
          ...prev,
          ...data,
          sendAt: moment(data.sendAt).format('YYYY-MM-DDTHH:mm'),
          reminderDate: data.reminderDate
            ? moment(data.reminderDate).format('YYYY-MM-DDTHH:mm')
            : '',
        }));

        // Restaurar template salvo ao editar agendamento
        if (data.templateName) {
          setSelectedTemplate({
            id: data.templateId,
            shortcode: data.templateName,
            message: data.templateBody,
          });
        }

        if (data.whatsapp) {
          setSelectedWhatsapps(data.whatsapp.id);
          setSelectedWhatsapp(data.whatsapp);
        }
        if (data.ticketUser) setSelectedUser(data.ticketUser);
        if (data.queueId) setSelectedQueue(data.queueId);
        if (data.intervalo) setIntervalo(data.intervalo);
        if (data.tipoDias) setTipoDias(data.tipoDias);
        setCurrentContact(data.contact || initialContact);
      } catch (err) {
        toastError(err);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, contactId, open, message]);

  const filterOptions = createFilterOptions({ trim: true });

  const handleClose = () => {
    onClose();
    setAttachment(null);
    setSchedule(getInitialState());
    setCurrentContact(initialContact);
    setSelectedTemplate(null);
    setQuickMessageSearch('');
    setShowAllQuickMessages(false);
    setFilteredQuickMessages([]);
    setQuickMessageMedia(null);
    if (attachmentFile.current) attachmentFile.current.value = null;
  };

  // Ao selecionar um template: salvar objeto completo e preencher campos Formik
  const handleTemplateChange = (shortcode, setFieldValue) => {
    const template = templates.find((t) => t.shortcode === shortcode);
    if (!template) {
      // Limpar seleção
      setSelectedTemplate(null);
      setFieldValue('templateId', '');
      setFieldValue('templateName', '');
      setFieldValue('templateBody', '');
      setFieldValue('templateData', null);
      return;
    }

    setSelectedTemplate(template);

    // Montar templateData compatível com IMetaMessageTemplate
    const templateData = {
      name: template.shortcode,
      language: { code: template.language || 'pt_BR' },
      components: template.components || [],
    };

    setFieldValue('body', template.message || '');
    setFieldValue('templateId', String(template.id));
    setFieldValue('templateName', template.shortcode);
    setFieldValue('templateBody', template.message || '');
    setFieldValue('templateData', templateData);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) setAttachment(file);
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case 'facebook':
        return (
          <Facebook style={{ color: '#3b5998', verticalAlign: 'middle' }} />
        );
      case 'instagram':
        return (
          <Instagram style={{ color: '#e1306c', verticalAlign: 'middle' }} />
        );
      case 'whatsapp':
        return (
          <WhatsApp style={{ color: '#25d366', verticalAlign: 'middle' }} />
        );
      default:
        return null;
    }
  };

  const renderOption = (option) => {
    if (option.name) {
      return (
        <>
          {IconChannel(option.channel)}
          <Typography
            component='span'
            style={{
              fontSize: 14,
              marginLeft: '10px',
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: '2',
            }}
          >
            {option.name}
          </Typography>
        </>
      );
    }
    return `${i18n.t('newTicketModal.add')} ${option.name}`;
  };

  const handleSaveSchedule = async (values) => {
    // Garantir que templateData seja objeto (não string)
    let templateData = values.templateData;
    if (typeof templateData === 'string') {
      try {
        templateData = JSON.parse(templateData);
      } catch {
        templateData = null;
      }
    }

    const scheduleData = {
      ...values,
      userId: user.id,
      whatsappId: selectedWhatsapps,
      ticketUserId: selectedUser?.id || null,
      queueId: selectedQueue || null,
      intervalo: intervalo || 1,
      tipoDias: tipoDias || 4,
      reminderDate: values.reminderDate || null,
      templateId: values.templateId || null,
      templateName: values.templateName || null,
      templateBody: values.templateBody || null,
      templateData: templateData || null,
    };

    try {
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, scheduleData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append('file', attachment);
          await api.post(`/schedules/${scheduleId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post('/schedules', scheduleData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append('file', attachment);
          await api.post(`/schedules/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t('scheduleModal.success'));
      if (typeof reload === 'function') reload();
      if (contactId && typeof cleanContact === 'function') {
        cleanContact();
        history.push('/schedules');
      }
    } catch (err) {
      toastError(err);
      return;
    }
    handleClose();
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    if (!el) return;
    const first = el.value.substring(0, el.selectionStart);
    const second = el.value.substring(el.selectionEnd);
    const newPos = el.selectionStart + msgVar.length;
    setValueFunc('body', `${first}${msgVar}${second}`);
    await new Promise((r) => setTimeout(r, 100));
    if (messageInputRef.current)
      messageInputRef.current.setSelectionRange(newPos, newPos);
  };

  const handleQuickMessageSelect = async (selectedMessage, setFieldValue) => {
    if (!selectedMessage) return;
    setFieldValue('body', selectedMessage.message || '');
    setQuickMessageSearch(selectedMessage.message || '');
    setShowAllQuickMessages(false);
    if (selectedMessage.mediaPath) {
      try {
        const f = await downloadQuickMessageMedia(
          selectedMessage.mediaPath,
          selectedMessage.mediaName,
          selectedMessage.mediaType,
        );
        if (f) {
          setAttachment(f);
          setQuickMessageMedia({
            path: selectedMessage.mediaPath,
            name: selectedMessage.mediaName,
            type: selectedMessage.mediaType,
          });
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      setQuickMessageMedia(null);
      setAttachment(null);
      if (attachmentFile.current) attachmentFile.current.value = null;
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      if (attachmentFile.current) attachmentFile.current.value = null;
    }
    if (quickMessageMedia) setQuickMessageMedia(null);
    if (schedule.mediaPath) {
      try {
        await api.delete(`/schedules/${schedule.id}/media-upload`);
        setSchedule((prev) => ({ ...prev, mediaPath: null }));
        toast.success(i18n.t('scheduleModal.toasts.deleted'));
        if (typeof reload === 'function') reload();
      } catch (err) {
        toastError(err);
      }
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t('scheduleModal.confirmationModal.deleteTitle')}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t('scheduleModal.confirmationModal.deleteMessage')}
      </ConfirmationModal>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        scroll='paper'
      >
        <DialogTitle id='form-dialog-title'>
          {schedule.status === 'ERRO'
            ? 'Erro de Envio'
            : `Mensagem ${capitalize(schedule.status || '')}`}
        </DialogTitle>

        <div style={{ display: 'none' }}>
          <input
            type='file'
            accept='.png,.jpg,.jpeg'
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>

        <Formik
          initialValues={schedule}
          enableReinitialize={true}
          validationSchema={ScheduleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveSchedule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => {
            setFieldValueRef.current = setFieldValue;
            return (
              <Form>
                <DialogContent dividers>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={6}>
                      <div className={classes.multFieldLine}>
                        <FormControl variant='outlined' fullWidth>
                          <Autocomplete
                            fullWidth
                            value={currentContact}
                            options={contacts}
                            onChange={(e, contact) => {
                              const id = contact ? contact.id : '';
                              setSchedule((prev) => ({
                                ...prev,
                                contactId: id,
                              }));
                              setFieldValue('contactId', id);
                              setCurrentContact(
                                contact ? contact : initialContact,
                              );
                              setChannelFilter(
                                contact ? contact.channel : 'whatsapp',
                              );
                            }}
                            getOptionLabel={(option) => option.name || ''}
                            renderOption={renderOption}
                            getOptionSelected={(option, value) =>
                              value.id === option.id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant='outlined'
                                placeholder='Contato'
                              />
                            )}
                          />
                        </FormControl>
                      </div>
                    </Grid>

                    <Grid item xs={12} md={6} xl={6}>
                      <Field
                        as={TextField}
                        label={i18n.t('scheduleModal.form.sendAt')}
                        type='datetime-local'
                        name='sendAt'
                        error={touched.sendAt && Boolean(errors.sendAt)}
                        helperText={touched.sendAt && errors.sendAt}
                        variant='outlined'
                        fullWidth
                        size='small'
                        style={{ marginTop: '8px' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>

                  <div className={classes.multFieldLine}>
                    <Field
                      as={TextField}
                      rows={9}
                      multiline={true}
                      label={i18n.t('scheduleModal.form.body')}
                      name='body'
                      inputRef={messageInputRef}
                      error={touched.body && Boolean(errors.body)}
                      helperText={touched.body && errors.body}
                      variant='outlined'
                      margin='dense'
                      fullWidth
                    />
                  </div>

                  {/* Busca de Respostas Rápidas */}
                  <div className={classes.multFieldLine}>
                    <TextField
                      variant='outlined'
                      fullWidth
                      margin='dense'
                      label={i18n.t('ticketInfo.quickMessages')}
                      placeholder='Digite para buscar respostas rápidas...'
                      value={quickMessageSearch}
                      onChange={(e) => {
                        setQuickMessageSearch(e.target.value);
                        if (e.target.value.trim() === '')
                          setShowAllQuickMessages(false);
                      }}
                      onFocus={() => {
                        if (
                          !quickMessageSearch.trim() &&
                          !showAllQuickMessages
                        ) {
                          setShowAllQuickMessages(true);
                          setFilteredQuickMessages(quickMessages);
                        }
                      }}
                      onClick={() => {
                        if (
                          !quickMessageSearch.trim() &&
                          !showAllQuickMessages
                        ) {
                          setShowAllQuickMessages(true);
                          setFilteredQuickMessages(quickMessages);
                        }
                      }}
                      disabled={loadingQuickMessages}
                      InputProps={{
                        startAdornment: (
                          <FlashOn
                            style={{ marginRight: '8px', color: '#1976d2' }}
                          />
                        ),
                      }}
                    />

                    {((quickMessageSearch &&
                      filteredQuickMessages.length > 0) ||
                      (showAllQuickMessages &&
                        filteredQuickMessages.length > 0)) && (
                      <div
                        style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          marginTop: '4px',
                          backgroundColor: 'white',
                          zIndex: 1000,
                          position: 'relative',
                        }}
                      >
                        {filteredQuickMessages.map((qm) => (
                          <QuickMessageItem
                            key={qm.id}
                            quickMessage={qm}
                            onSelect={(q) =>
                              handleQuickMessageSelect(q, setFieldValue)
                            }
                          />
                        ))}
                      </div>
                    )}

                    {quickMessageSearch &&
                      filteredQuickMessages.length === 0 &&
                      !loadingQuickMessages && (
                        <div
                          style={{
                            padding: '12px',
                            textAlign: 'center',
                            color: '#666',
                            fontStyle: 'italic',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            marginTop: '4px',
                          }}
                        >
                          Nenhuma resposta rápida encontrada
                        </div>
                      )}
                  </div>

                  <Grid item xs={12} md={12} xl={12}>
                    <MessageVariablesPicker
                      disabled={isSubmitting}
                      showSchedulingVars={true}
                      onClick={(value) =>
                        handleClickMsgVar(value, setFieldValue)
                      }
                    />
                  </Grid>

                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={3}>
                      <FormControl
                        variant='outlined'
                        margin='dense'
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id='whatsapp-selection-label'>
                          {i18n.t('campaigns.dialog.form.whatsapp')}
                        </InputLabel>
                        <Select
                          label={i18n.t('campaigns.dialog.form.whatsapp')}
                          labelId='whatsapp-selection-label'
                          id='whatsappIds'
                          name='whatsappIds'
                          required
                          value={selectedWhatsapps}
                          onChange={(e) => {
                            const sel = whatsapps.find(
                              (w) => w.id === e.target.value,
                            );
                            setSelectedWhatsapps(e.target.value);
                            setSelectedWhatsapp(sel || null);
                            // Limpar template ao trocar conexão
                            setSelectedTemplate(null);
                            setFieldValue('templateId', '');
                            setFieldValue('templateName', '');
                            setFieldValue('templateBody', '');
                            setFieldValue('templateData', null);
                          }}
                        >
                          {whatsapps.map((w) => (
                            <MenuItem key={w.id} value={w.id}>
                              {w.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* ✅ Seletor de template — só aparece para whatsapp_oficial */}
                    {isOficialChannel && (
                      <Grid item xs={12} md={6} xl={6}>
                        {templates.length > 0 ? (
                          <>
                            <FormControl
                              variant='outlined'
                              margin='dense'
                              fullWidth
                              className={classes.formControl}
                            >
                              <InputLabel id='template-selection-label'>
                                Template Meta (mensagem oficial)
                              </InputLabel>
                              <Select
                                label='Template Meta (mensagem oficial)'
                                labelId='template-selection-label'
                                value={selectedTemplate?.shortcode || ''}
                                onChange={(e) =>
                                  handleTemplateChange(
                                    e.target.value,
                                    setFieldValue,
                                  )
                                }
                              >
                                <MenuItem value=''>
                                  <em>
                                    Nenhum (texto livre — só funciona dentro de
                                    24h)
                                  </em>
                                </MenuItem>
                                {templates.map((t) => (
                                  <MenuItem key={t.id} value={t.shortcode}>
                                    {t.shortcode}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {selectedTemplate && (
                              <Typography
                                variant='caption'
                                style={{
                                  color: '#1976d2',
                                  display: 'block',
                                  marginTop: 2,
                                }}
                              >
                                ✓ Template selecionado:{' '}
                                <strong>{selectedTemplate.shortcode}</strong>
                              </Typography>
                            )}
                            {!selectedTemplate && (
                              <Typography
                                variant='caption'
                                style={{
                                  color: '#e65100',
                                  display: 'block',
                                  marginTop: 2,
                                }}
                              >
                                ⚠️ Sem template: a mensagem só será entregue
                                dentro da janela de 24h.
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography
                            variant='caption'
                            style={{
                              color: '#999',
                              display: 'block',
                              marginTop: 12,
                            }}
                          >
                            Nenhum template oficial encontrado. Cadastre
                            quick-messages com isOficial=true e status=APPROVED.
                          </Typography>
                        )}

                        {/* Campos ocultos Formik para template */}
                        <Field type='hidden' name='templateId' />
                        <Field type='hidden' name='templateName' />
                        <Field type='hidden' name='templateBody' />
                      </Grid>
                    )}

                    <Grid item xs={12} md={12} xl={3}>
                      <FormControl
                        variant='outlined'
                        margin='dense'
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id='openTicket-selection-label'>
                          {i18n.t('campaigns.dialog.form.openTicket')}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t('campaigns.dialog.form.openTicket')}
                          labelId='openTicket-selection-label'
                          id='openTicket'
                          name='openTicket'
                          error={
                            touched.openTicket && Boolean(errors.openTicket)
                          }
                        >
                          <MenuItem value={'enabled'}>
                            {i18n.t('campaigns.dialog.form.enabledOpenTicket')}
                          </MenuItem>
                          <MenuItem value={'disabled'}>
                            {i18n.t('campaigns.dialog.form.disabledOpenTicket')}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid spacing={1} container>
                    <Grid item xs={12} md={6} xl={4}>
                      <Autocomplete
                        style={{ marginTop: '8px' }}
                        variant='outlined'
                        margin='dense'
                        className={classes.formControl}
                        getOptionLabel={(option) => `${option.name}`}
                        value={isAdmin ? selectedUser : user}
                        size='small'
                        onChange={(e, newValue) => {
                          if (!isAdmin) return;
                          setSelectedUser(newValue);
                          if (
                            newValue != null &&
                            Array.isArray(newValue.queues)
                          ) {
                            if (newValue.queues.length === 1)
                              setSelectedQueue(newValue.queues[0].id);
                            setQueues(newValue.queues);
                          } else {
                            setQueues(allQueues);
                            setSelectedQueue('');
                          }
                        }}
                        options={isAdmin ? options : [user]}
                        filterOptions={filterOptions}
                        freeSolo={isAdmin}
                        fullWidth
                        disabled={values.openTicket === 'disabled' || !isAdmin}
                        autoHighlight
                        noOptionsText={i18n.t('transferTicketModal.noOptions')}
                        loading={loading}
                        renderOption={(option) => (
                          <span>
                            <UserStatusIcon user={option} /> {option.name}
                          </span>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={i18n.t('transferTicketModal.fieldLabel')}
                            variant='outlined'
                            onChange={
                              isAdmin
                                ? (e) => setSearchParam(e.target.value)
                                : undefined
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {loading ? (
                                    <CircularProgress
                                      color='inherit'
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                              readOnly: !isAdmin,
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6} xl={6}>
                      <FormControl
                        variant='outlined'
                        margin='dense'
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel>
                          {i18n.t('transferTicketModal.fieldQueueLabel')}
                        </InputLabel>
                        <Select
                          value={selectedQueue}
                          onChange={(e) => setSelectedQueue(e.target.value)}
                          label={i18n.t(
                            'transferTicketModal.fieldQueuePlaceholder',
                          )}
                          disabled={values.openTicket === 'disabled'}
                        >
                          {queues.map((q) => (
                            <MenuItem key={q.id} value={q.id}>
                              {q.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid spacing={1} container style={{ marginTop: '-10px' }}>
                    <Grid item xs={12} md={6} xl={6}>
                      <FormControl
                        variant='outlined'
                        margin='dense'
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id='statusTicket-selection-label'>
                          {i18n.t('campaigns.dialog.form.statusTicket')}
                        </InputLabel>
                        <Field
                          as={Select}
                          disabled={values.openTicket === 'disabled'}
                          label={i18n.t('campaigns.dialog.form.statusTicket')}
                          labelId='statusTicket-selection-label'
                          id='statusTicket'
                          name='statusTicket'
                          error={
                            touched.statusTicket && Boolean(errors.statusTicket)
                          }
                        >
                          <MenuItem value={'closed'}>
                            {i18n.t('campaigns.dialog.form.closedTicketStatus')}
                          </MenuItem>
                          <MenuItem value={'open'}>
                            {i18n.t('campaigns.dialog.form.openTicketStatus')}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6} xl={6}>
                      <FormControlLabel
                        control={
                          <Field
                            as={Switch}
                            color='primary'
                            name='assinar'
                            checked={values.assinar}
                            disabled={values.openTicket === 'disabled'}
                          />
                        }
                        label={i18n.t('scheduleModal.form.assinar')}
                      />
                    </Grid>
                  </Grid>

                  <br />

                  {/* Seção de Lembrete */}
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <h3>Lembrete (Opcional)</h3>
                      <p>
                        Defina uma data de lembrete. A mensagem será enviada no
                        horário do lembrete em vez do horário original do
                        agendamento.
                      </p>
                    </Grid>
                    <Grid item xs={12} md={6} xl={6}>
                      <Field
                        as={TextField}
                        label='Data do Lembrete'
                        type='datetime-local'
                        name='reminderDate'
                        error={
                          touched.reminderDate && Boolean(errors.reminderDate)
                        }
                        helperText={touched.reminderDate && errors.reminderDate}
                        variant='outlined'
                        fullWidth
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} xl={6}>
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        style={{
                          padding: '16px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                          marginTop: '8px',
                        }}
                      >
                        A mensagem do lembrete será a mesma da mensagem
                        agendada.
                      </Typography>
                    </Grid>
                  </Grid>

                  <br />

                  <h3>{i18n.t('recurrenceSection.title')}</h3>
                  <p>{i18n.t('recurrenceSection.description')}</p>
                  <br />

                  <Grid container spacing={1}>
                    <Grid item xs={12} md={4} xl={4}>
                      <FormControl size='small' fullWidth variant='outlined'>
                        <InputLabel id='intervalo-label'>
                          {i18n.t('recurrenceSection.labelInterval')}
                        </InputLabel>
                        <Select
                          labelId='intervalo-label'
                          value={intervalo}
                          onChange={(e) => setIntervalo(e.target.value || 1)}
                          label={i18n.t('recurrenceSection.labelInterval')}
                        >
                          <MenuItem value={1}>
                            {i18n.t('recurrenceSection.options.days')}
                          </MenuItem>
                          <MenuItem value={2}>
                            {i18n.t('recurrenceSection.options.weeks')}
                          </MenuItem>
                          <MenuItem value={3}>
                            {i18n.t('recurrenceSection.options.months')}
                          </MenuItem>
                          <MenuItem value={4}>
                            {i18n.t('recurrenceSection.options.minutes')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4} xl={4}>
                      <Field
                        as={TextField}
                        label={i18n.t('recurrenceSection.intervalFilterValue')}
                        name='valorIntervalo'
                        size='small'
                        type='number'
                        inputProps={{ min: 0 }}
                        error={
                          touched.valorIntervalo &&
                          Boolean(errors.valorIntervalo)
                        }
                        helperText={
                          touched.valorIntervalo && errors.valorIntervalo
                        }
                        InputLabelProps={{ shrink: true }}
                        variant='outlined'
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} md={4} xl={4}>
                      <Field
                        as={TextField}
                        label={i18n.t('recurrenceSection.sendAsManyTimes')}
                        name='enviarQuantasVezes'
                        size='small'
                        type='number'
                        inputProps={{ min: 0 }}
                        error={
                          touched.enviarQuantasVezes &&
                          Boolean(errors.enviarQuantasVezes)
                        }
                        helperText={
                          touched.enviarQuantasVezes &&
                          errors.enviarQuantasVezes
                        }
                        variant='outlined'
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} md={12} xl={12}>
                      <FormControl size='small' fullWidth variant='outlined'>
                        <InputLabel id='tipoDias-label'>
                          {i18n.t('recurrenceSection.sendAsManyTimes')}
                        </InputLabel>
                        <Select
                          labelId='tipoDias-label'
                          value={tipoDias}
                          onChange={(e) => setTipoDias(e.target.value || 4)}
                          label={i18n.t('recurrenceSection.sendAsManyTimes')}
                        >
                          <MenuItem value={4}>
                            {i18n.t(
                              'recurrenceSection.shipNormallyOnNonbusinessDays',
                            )}
                          </MenuItem>
                          <MenuItem value={5}>
                            {i18n.t(
                              'recurrenceSection.sendOneBusinessDayBefore',
                            )}
                          </MenuItem>
                          <MenuItem value={6}>
                            {i18n.t(
                              'recurrenceSection.sendOneBusinessDayLater',
                            )}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {(schedule.mediaPath || attachment || quickMessageMedia) && (
                    <Grid xs={12} item>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Button startIcon={<AttachFile />}>
                          {attachment
                            ? attachment.name
                            : quickMessageMedia
                              ? quickMessageMedia.name
                              : schedule.mediaName}
                        </Button>
                        {quickMessageMedia && (
                          <Chip
                            label='Da Resposta Rápida'
                            size='small'
                            color='primary'
                            variant='outlined'
                          />
                        )}
                        <IconButton
                          onClick={() => setConfirmationOpen(true)}
                          color='secondary'
                        >
                          <DeleteOutline color='secondary' />
                        </IconButton>
                      </div>
                    </Grid>
                  )}
                </DialogContent>

                <DialogActions>
                  {schedule.sentAt && schedule.ticket?.uuid && (
                    <Button
                      onClick={() =>
                        history.push(`/tickets/${schedule.ticket.uuid}`)
                      }
                      color='primary'
                      variant='outlined'
                    >
                      Ir para conversa
                    </Button>
                  )}
                  {!attachment && !schedule.mediaPath && !quickMessageMedia && (
                    <Button
                      color='primary'
                      onClick={() => attachmentFile.current.click()}
                      disabled={isSubmitting}
                      variant='outlined'
                    >
                      {i18n.t('quickMessages.buttons.attach')}
                    </Button>
                  )}
                  <Button
                    onClick={handleClose}
                    color='secondary'
                    disabled={isSubmitting}
                    variant='outlined'
                  >
                    {i18n.t('scheduleModal.buttons.cancel')}
                  </Button>
                  {(schedule.sentAt === null || schedule.sentAt === '') && (
                    <Button
                      type='submit'
                      color='primary'
                      disabled={isSubmitting}
                      variant='contained'
                      className={classes.btnWrapper}
                    >
                      {scheduleId
                        ? `${i18n.t('scheduleModal.buttons.okEdit')}`
                        : `${i18n.t('scheduleModal.buttons.okAdd')}`}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </Button>
                  )}
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>

      {/* TemplateMetaModal — fallback visual se preferir selecionar via modal */}
      <TemplateMetaModal
        open={templateModalOpen}
        handleClose={() => setTemplateModalOpen(false)}
        templates={templates}
        onSelectTemplate={(template) => {
          setTemplateModalOpen(false);
          if (setFieldValueRef.current) {
            handleTemplateChange(template.shortcode, setFieldValueRef.current);
          }
        }}
      />
    </div>
  );
};

const QuickMessageItem = ({ quickMessage, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={() => onSelect(quickMessage)}
      style={{
        padding: '12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: hovered ? '#f5f5f5' : 'white',
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <FlashOn style={{ fontSize: '16px', color: '#1976d2', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
          {quickMessage.shortcode}
        </div>
        <div
          style={{
            fontSize: '12px',
            color: '#666',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {quickMessage.message}
        </div>
      </div>
      {quickMessage.mediaPath && (
        <AttachFile
          style={{ fontSize: '14px', color: '#666', flexShrink: 0 }}
        />
      )}
    </div>
  );
};

export default ScheduleModal;
